import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Mail, Trash2 } from "lucide-react";
import type { MemberRole } from "@dishfolio/contracts";
import { Badge, Button, Card, CardDescription, CardHeader, CardTitle, ConfirmDialog, Field, Input, NativeSelect, toast } from "@dishfolio/ui";
import { api } from "../lib/api.ts";
import { useOrg } from "../lib/org.tsx";
import { dateTime } from "../lib/format.ts";
import { PageContainer, PageHeader } from "../components/Page.tsx";
import { notifyError } from "../components/Upgrade.tsx";

type Members = {
  members: Array<{ id: string; userId: string; name: string; email: string; role: MemberRole; createdAt: string }>;
  invitations: Array<{ id: string; email: string; role: string; expiresAt: string }>;
  maxMembers: number;
  assignableRoles: MemberRole[];
};

const ROLE_HELP: Record<MemberRole, string> = {
  owner: "Everything, including billing and deleting the organization.",
  admin: "Manage locations, menus, team (except admins) and orders.",
  editor: "Edit and publish menus, translations and imports.",
  staff: "Order board and item availability only.",
};

export function TeamPage() {
  const { me, can } = useOrg();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["members"], queryFn: () => api.get<Members>("/organization/members") });
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<MemberRole>("staff");
  const [link, setLink] = useState<string | null>(null);
  const [remove, setRemove] = useState<string | null>(null);
  const d = q.data;
  const refresh = () => qc.invalidateQueries({ queryKey: ["members"] });
  const used = (d?.members.length ?? 0) + (d?.invitations.length ?? 0);
  return (
    <PageContainer>
      <PageHeader title="Team" description={d ? `${d.members.length} member${d.members.length === 1 ? "" : "s"} · ${d.maxMembers} staff accounts included in your plan` : undefined} />
      <div className="grid gap-6">
        {can("members:manage") ? (
          <Card>
            <CardHeader>
              <CardTitle>Invite someone</CardTitle>
              <CardDescription>{ROLE_HELP[role]}</CardDescription>
            </CardHeader>
            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  const res = await api.post<{ link: string }>("/organization/invitations", { email, role });
                  setLink(res.link);
                  setEmail("");
                  toast.success("Invitation sent");
                  await refresh();
                } catch (err) {
                  notifyError(err);
                }
              }}
            >
              <Field label="Email" className="min-w-60 flex-1">
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </Field>
              <Field label="Role">
                <NativeSelect value={role} onChange={(e) => setRole(e.target.value as MemberRole)}>
                  {d?.assignableRoles.map((r) => (
                    <option key={r} value={r} className="capitalize">
                      {r}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Button type="submit" disabled={Boolean(d && used >= d.maxMembers)}>
                <Mail /> Invite
              </Button>
            </form>
            {d && used >= d.maxMembers ? <p className="text-xs text-muted-foreground">You’ve used all staff accounts on your plan. Upgrade for more.</p> : null}
            {link ? (
              <div className="flex items-center gap-2 rounded-md border bg-muted/50 p-2 text-xs">
                <span className="flex-1 truncate font-mono">{link}</span>
                <Button size="sm" variant="ghost" onClick={() => (navigator.clipboard.writeText(link), toast.success("Copied"))}>
                  <Copy />
                </Button>
              </div>
            ) : null}
          </Card>
        ) : null}
        <Card>
          <CardTitle>Members</CardTitle>
          <ul className="divide-y">
            {d?.members.map((m) => (
              <li key={m.id} className="flex flex-wrap items-center gap-3 py-3">
                <span className="flex size-8 items-center justify-center rounded-full bg-secondary text-sm font-semibold">{m.name.slice(0, 1).toUpperCase()}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-medium">
                    {m.name} {m.userId === me.user.id ? <span className="text-xs text-muted-foreground">(you)</span> : null}
                  </div>
                  <div className="truncate text-sm text-muted-foreground">{m.email}</div>
                </div>
                {can("members:manage") && m.role !== "owner" && d.assignableRoles.includes(m.role) ? (
                  <NativeSelect
                    className="w-auto"
                    value={m.role}
                    onChange={async (e) => {
                      try {
                        await api.patch(`/organization/members/${m.id}`, { role: e.target.value });
                        await refresh();
                      } catch (err) {
                        notifyError(err);
                      }
                    }}
                  >
                    {d.assignableRoles.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </NativeSelect>
                ) : (
                  <Badge variant="secondary" className="capitalize">
                    {m.role}
                  </Badge>
                )}
                {m.role !== "owner" && (can("members:manage") || m.userId === me.user.id) ? (
                  <Button variant="ghost" size="icon-sm" onClick={() => setRemove(m.id)} aria-label="Remove member">
                    <Trash2 />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
        {d?.invitations.length ? (
          <Card>
            <CardTitle>Pending invitations</CardTitle>
            <ul className="divide-y">
              {d.invitations.map((i) => (
                <li key={i.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                  <span>
                    {i.email} <Badge variant="outline" className="ml-1 capitalize">{i.role}</Badge>
                  </span>
                  <span className="text-xs text-muted-foreground">Expires {dateTime(i.expiresAt)}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      await api.del(`/organization/invitations/${i.id}`);
                      await refresh();
                    }}
                  >
                    Revoke
                  </Button>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}
      </div>
      <ConfirmDialog
        open={Boolean(remove)}
        onOpenChange={(o) => !o && setRemove(null)}
        title="Remove this member?"
        description="They lose access to this organization immediately."
        destructive
        confirmLabel="Remove"
        onConfirm={async () => {
          try {
            await api.del(`/organization/members/${remove}`);
            await refresh();
          } catch (err) {
            notifyError(err);
          }
        }}
      />
    </PageContainer>
  );
}
